import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDiscussionApi } from "../../hooks/useDiscussionApi";
import { ReplyForm } from "./CommentForms";
import Comment from "./Comment";
import { CommentErrorBoundary } from "./CommentSkeleton";
import { API_ENDPOINTS } from "../../config/constants";

const DiscussionThread = ({ vehicleId, user }) => {
  const { t } = useTranslation();
  const [threadState, setThreadState] = useState({
    discussions: [],
    isCollapsed: false,
    expandedComments: [],
    newComment: "",
    error: "",
    highlightedCommentId: null,
  });

  const api = useDiscussionApi();

  // Helper function to scroll to and highlight a comment
  const scrollToComment = useCallback((commentId) => {
    const element = document.getElementById(`comment-${commentId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      setThreadState((prev) => ({ ...prev, highlightedCommentId: commentId }));
      // Remove highlight after animation (increased from 2s to 3s)
      setTimeout(() => {
        setThreadState((prev) => ({ ...prev, highlightedCommentId: null }));
      }, 3000);
    }
  }, []);

  const fetchDiscussions = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.VEHICLE_DISCUSSIONS(vehicleId));
      const data = await res.json();
      setThreadState((prev) => ({ ...prev, discussions: data }));
    } catch (err) {
      console.error("Error fetching discussions:", err);
      setThreadState((prev) => ({
        ...prev,
        error: t("common.error"),
      }));
    }
  }, [vehicleId, t]);

  useEffect(() => {
    fetchDiscussions();
  }, [fetchDiscussions]);

  const handleNewCommentSubmit = async (e) => {
    e.preventDefault();
    if (!threadState.newComment.trim()) return;

    try {
      const newComment = await api.createComment(
        vehicleId,
        threadState.newComment
      );
      setThreadState((prev) => ({
        ...prev,
        discussions: [newComment, ...prev.discussions],
        newComment: "",
        error: "",
      }));
      // Add a small delay before scrolling to ensure the DOM has updated
      setTimeout(() => {
        scrollToComment(newComment._id);
      }, 100);
    } catch (err) {
      setThreadState((prev) => ({ ...prev, error: err.message }));
    }
  };

  return (
    <div className="bg-gray-50 p-3 rounded-lg dark:bg-gray-800">
      <div className="flex items-center gap-2">
        <button
          onClick={() =>
            setThreadState((prev) => ({
              ...prev,
              isCollapsed: !prev.isCollapsed,
            }))
          }
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 w-full dark:text-gray-400 dark:hover:text-gray-200"
        >
          <span>{threadState.isCollapsed ? "+" : "-"}</span>
          <h3 className="text-base font-semibold">{t("discussion.title")}</h3>
        </button>
      </div>

      {!threadState.isCollapsed && (
        <>
          <div className="mt-4">
            {threadState.discussions.map((disc) => (
              <CommentErrorBoundary key={disc._id}>
                <Comment
                  comment={disc}
                  user={user}
                  refreshThread={fetchDiscussions}
                  expandedComments={threadState.expandedComments}
                  setExpandedComments={(comments) =>
                    setThreadState((prev) => ({
                      ...prev,
                      expandedComments: comments,
                    }))
                  }
                  depth={0}
                  vehicleId={vehicleId}
                  isHighlighted={disc._id === threadState.highlightedCommentId}
                />
              </CommentErrorBoundary>
            ))}
          </div>
          {user ? (
            <ReplyForm
              onSubmit={handleNewCommentSubmit}
              text={threadState.newComment}
              setText={(text) =>
                setThreadState((prev) => ({ ...prev, newComment: text }))
              }
              error={threadState.error}
            />
          ) : (
            <p className="text-sm text-gray-600">
              {t("discussion.login_required")}
            </p>
          )}
        </>
      )}
    </div>
  );
};

DiscussionThread.propTypes = {
  vehicleId: PropTypes.string.isRequired,
  user: PropTypes.object,
};

export default DiscussionThread;
